import { API_ENDPOINTS } from '../api/ApiConfig';
import { HttpClient } from '../api/HttpClient';
import type {
  Album,
  AlbumsData,
  AlbumsQueryParams,
  CreateAlbumRequest,
  UpdateAlbumRequest,
} from '../../types/api/album';

export class AlbumService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  async getAlbums(params: AlbumsQueryParams = {}): Promise<AlbumsData> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.append('size', params.size.toString());
    }
    if (params.keyword) {
      queryParams.append('keyword', params.keyword);
    }

    const url = `${API_ENDPOINTS.ALBUMS.ADMIN_LIST}?${queryParams.toString()}`;
    const response = await this.httpClient.get<AlbumsData>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch albums');
    }

    return response.data;
  }

  async createAlbumMultipart(
    albumData: CreateAlbumRequest,
    coverImageFile?: File,
    imageFile?: File
  ): Promise<Album> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(albumData)], { type: 'application/json' });

    formData.append('data', jsonBlob, 'data.json');
    if (coverImageFile) {
      formData.append('coverImage', coverImageFile, coverImageFile.name);
    }
    if (imageFile) {
      formData.append('image', imageFile, imageFile.name);
    }

    const response = await this.httpClient.request<Album>({
      method: 'POST',
      url: API_ENDPOINTS.ALBUMS.ADMIN_CREATE_MULTIPART,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create album');
    }

    return response.data;
  }

  async getAlbumById(albumId: string): Promise<Album> {
    const response = await this.httpClient.get<Album>(API_ENDPOINTS.ALBUMS.ADMIN_DETAIL(albumId));

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch album');
    }

    return response.data;
  }

  async updateAlbumMultipart(
    albumId: string,
    albumData: UpdateAlbumRequest,
    coverImageFile?: File,
    imageFile?: File
  ): Promise<Album> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(albumData)], { type: 'application/json' });

    formData.append('data', jsonBlob, 'data.json');
    if (coverImageFile) {
      formData.append('coverImage', coverImageFile, coverImageFile.name);
    }
    if (imageFile) {
      formData.append('image', imageFile, imageFile.name);
    }

    const response = await this.httpClient.request<Album>({
      method: 'PUT',
      url: API_ENDPOINTS.ALBUMS.ADMIN_UPDATE_MULTIPART(albumId),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update album');
    }

    return response.data;
  }

  async deleteAlbum(albumId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      API_ENDPOINTS.ALBUMS.ADMIN_DELETE(albumId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete album');
    }
  }
}
